import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loadManorState, loginManor, startManorGame, syncReport } from './manorSlice';
import ManorHeader from './ManorHeader';
import usePortrait from './usePortrait';
import './manor.css';
import './stages.css';
import './portrait.css';

// Where the player belongs, given their saved progress (mirrors gameLoc.php).
export function routeForState(game) {
  if (!game) return '/manor/stage1';
  if (game.gameover) return '/manor/thankyou';
  return `/manor/stage${game.nextStage}`;
}

/**
 * Full-screen page frame: background (landscape art, or the phone-portrait art when the
 * device is upright) and the session / progress guard. `stage` (1-5) makes the shell redirect
 * a player who has already progressed past (or not yet reached) that stage.
 *
 *  bg           landscape background, relative to /manor/img/
 *  bgMob        portrait background, relative to /manor/ (a purpose-made phone image)
 *  portraitFit  when there is no bgMob: 'cover' (default) or 'contain' — the whole landscape
 *               scene at full width over a blurred copy, for scenes whose props sit on the art
 */
export default function ManorShell({ bg, bgMob, portraitFit = 'cover', bgSize = '100% 100%', stage, children, className = '' }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { session, game, loaded, authStatus, authError, starting, startTried, reportId, reportTried } = useSelector((s) => s.manor);
  const portrait = usePortrait();

  useEffect(() => {
    if (!session && authStatus === 'idle') dispatch(loginManor());
  }, [session, authStatus, dispatch]);

  useEffect(() => {
    if (session && !loaded) dispatch(loadManorState());
  }, [session, loaded, dispatch]);

  // Logging in creates the player's gameattach + stat3 rows straight away (idempotent on the server).
  useEffect(() => {
    if (session && loaded && !game && !starting && !startTried) dispatch(startManorGame());
  }, [session, loaded, game, starting, startTried, dispatch]);

  // A player with no report yet (first visit, or an earlier report call failed) gets one created now.
  useEffect(() => {
    if (session && game && !reportId && !reportTried) dispatch(syncReport({ points: game.points, time: game.time }));
  }, [session, game, reportId, reportTried, dispatch]);

  useEffect(() => {
    if (!loaded || !stage) return;
    const target = routeForState(game);
    const here = stage === 'thankyou' ? '/manor/thankyou' : `/manor/stage${stage}`;
    if (target !== here) navigate(target, { replace: true });
  }, [loaded, game, stage, navigate]);

  if (!session) {
    if (authStatus !== 'failed') {
      return (
        <div className="manor-page manor-message">
          <p>Loading…</p>
        </div>
      );
    }
    return (
      <div className="manor-page manor-message">
        <p>{authError || 'Please launch The Haunting of Hollow Manor from ExtraMilePlay.'}</p>
        <a href={process.env.REACT_APP_BASE_URL}>Go to ExtraMilePlay</a>
      </div>
    );
  }

  // A stage page renders only once progress is known and this is the stage the player is up to.
  const here = stage === 'thankyou' ? '/manor/thankyou' : `/manor/stage${stage}`;
  const ready = !stage || (loaded && routeForState(game) === here);

  let image = bg ? `/manor/img/${bg}` : null;
  let size = bgSize;
  let blur = false;
  if (portrait) {
    if (bgMob) {
      image = `/manor/${bgMob}`;
      size = 'cover';
    } else if (image) {
      blur = portraitFit === 'contain';
      size = blur ? '100% auto' : 'cover';
    }
  }

  return (
    <div
      className={`manor-page ${portrait ? 'is-portrait' : 'is-landscape'} ${className}`}
      style={image && !blur ? { backgroundImage: `url(${image})`, backgroundSize: size } : undefined}
    >
      {blur && (
        <>
          <div className="manor-bgblur" style={{ backgroundImage: `url(${image})` }} />
          <div className="manor-bgsharp" style={{ backgroundImage: `url(${image})` }} />
        </>
      )}
      <ManorHeader />
      {ready && children}
    </div>
  );
}
